import { adminFetch } from './apiClient';
import { CATEGORIES } from '../utils/constants';

const STORAGE_KEY = 'portal_categories';

const save = (cats) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cats));
  } catch (e) {
    console.error('categoryService: save failed', e);
  }
};

export const getAll = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) {
        // Merge any new built-in CATEGORIES not yet present in stored data
        const storedSlugs = new Set(parsed.map(c => c.slug));
        const missing = CATEGORIES.filter(c => !storedSlugs.has(c.slug));
        if (missing.length > 0) {
          const maxOrder = Math.max(...parsed.map(c => c.order || 0));
          const merged = [
            ...parsed,
            ...missing.map((cat, i) => ({
              ...cat,
              id: cat.slug,
              isActive: true,
              showOnHomepage: false,
              order: maxOrder + i + 1,
              isDefault: true,
            })),
          ];
          save(merged);
          return merged;
        }
        return parsed;
      }
    }
  } catch {}
  // Seed from the hardcoded CATEGORIES constant on first run
  const seeded = CATEGORIES.map((cat, i) => ({
    ...cat,
    id: cat.slug,
    isActive: true,
    showOnHomepage: true,
    order: i,
    isDefault: true,
  }));
  save(seeded);
  return seeded;
};

export const addCategory = (data) => {
  const cats = getAll();
  const slug = data.slug
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  if (cats.find((c) => c.slug === slug)) {
    throw new Error(`A category with slug "${slug}" already exists`);
  }
  const newCat = {
    id: 'cat_' + Date.now(),
    slug,
    label: data.label.trim(),
    labelEn: data.labelEn.trim(),
    emoji: data.emoji || '📰',
    color: data.color || '#6B7280',
    isActive: true,
    showOnHomepage: true,
    order: cats.length > 0 ? Math.max(...cats.map((c) => c.order)) + 1 : 0,
    isDefault: false,
  };
  cats.push(newCat);
  save(cats);
  adminFetch('/api/admin/categories', { method: 'POST', body: JSON.stringify(newCat) }).catch(() => {});
  return newCat;
};

export const updateCategory = (id, updates) => {
  const cats = getAll();
  const idx = cats.findIndex((c) => c.id === id);
  if (idx === -1) return null;
  cats[idx] = { ...cats[idx], ...updates };
  save(cats);
  adminFetch(`/api/admin/categories/${id}`, { method: 'PUT', body: JSON.stringify(cats[idx]) }).catch(() => {});
  return cats[idx];
};

// Built-in (isDefault) categories can be disabled but not deleted
export const deleteCategory = (id) => {
  const cats = getAll();
  const cat = cats.find((c) => c.id === id);
  if (!cat) return false;
  if (cat.isDefault) return false;
  save(cats.filter((c) => c.id !== id));
  adminFetch(`/api/admin/categories/${id}`, { method: 'DELETE' }).catch(() => {});
  return true;
};

export const reorderCategories = (ordered) => {
  const withOrder = ordered.map((c, i) => ({ ...c, order: i }));
  save(withOrder);
  adminFetch('/api/admin/categories/reorder', {
    method: 'POST',
    body: JSON.stringify({ categories: withOrder.map(c => ({ id: c.id, order: c.order })) }),
  }).catch(() => {});
};

// Fetch categories from backend; seed backend from localStorage if empty
export const syncCategoriesFromBackend = async () => {
  try {
    const res = await adminFetch('/api/admin/categories');
    if (!res.ok) return null;
    const data = await res.json();
    const cats = (data.categories || []).sort((a, b) => (a.order || 0) - (b.order || 0));
    if (cats.length > 0) {
      save(cats);
      return cats;
    }
    // Backend empty — push local data up once
    const local = getAll();
    if (local.length > 0) {
      await Promise.allSettled(
        local.map(cat => adminFetch('/api/admin/categories', { method: 'POST', body: JSON.stringify(cat) }))
      );
    }
    return local;
  } catch {
    return null;
  }
};
